import { holdsAnywhere } from '@carii/casl-abilities';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select } from '../../components/ui/FormField.js';
import { GapWarning } from '../../components/ui/GapWarning.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { ApiError, api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { TRIP_PURPOSES, TRIP_PURPOSE_LABELS } from '../../utils/tripLabels.js';

interface CustomerOption {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  identity_verification?: { status: string };
}

interface UsersResponse {
  items: CustomerOption[];
}

interface CarOption {
  _id: string;
  name: string;
  plate_number: string;
}

interface CarsResponse {
  items: CarOption[];
}

interface PickupPlace {
  _id: string;
  name: string;
}

interface LocationsResponse {
  items: PickupPlace[];
}

interface QuotePreview {
  outcome: string;
  pricing_template_id?: string;
  currency_symbol?: string;
  total_minor?: number;
  grand_total_minor?: number;
}

interface CreatedBooking {
  id: string;
}

/** 202 (#287): staff record an already-happened rental for an existing customer, with a past
 *  pickup/return window — the one thing the ordinary customer-facing form refuses. */
/** 393: the picked customer's unpaid penalties, as a warning. */
function UnpaidPenaltyWarning({ userId, name }: { userId: string; name: string }) {
  const { data } = useQuery({
    queryKey: ['penalties', 'unpaid-of', userId],
    queryFn: () =>
      api.get(`/penalties?user_id=${userId}&paid=unpaid&status=active`) as Promise<{
        items: Array<{ amount_minor: number }>;
      }>,
  });
  const items = data?.items ?? [];
  if (items.length === 0) return null;
  const total = items.reduce((s, p) => s + Number(p.amount_minor || 0), 0);
  return (
    <p
      data-testid="penalty-warning"
      className="rounded-lg bg-yellow-50 px-3 py-2 text-sm text-yellow-900 dark:bg-yellow-500/10 dark:text-yellow-200"
    >
      <strong>
        {name || 'This customer'} has an unpaid penalty (RM {(total / 100).toFixed(2)}).
      </strong>{' '}
      They cannot book in the app until it is paid and approved. You can still create this booking.{' '}
      <a href="/penalties" className="text-primary underline">
        See penalties
      </a>
    </p>
  );
}

export function BookingCreatePage() {
  const ability = useAbility();
  const canManage = ability.can('manage', 'Booking');
  const navigate = useNavigate();

  // Customer search — mirrors UserListPage's debounced email search.
  const [customerEmailInput, setCustomerEmailInput] = useState('');
  const [customerEmailQuery, setCustomerEmailQuery] = useState('');
  const [customerId, setCustomerId] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setCustomerEmailQuery(customerEmailInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [customerEmailInput]);

  const { data: customers } = useQuery({
    queryKey: ['admin-users-search', customerEmailQuery],
    queryFn: () =>
      api.get<UsersResponse>(`/users?limit=10&email=${encodeURIComponent(customerEmailQuery)}`),
    enabled: canManage && customerEmailQuery.length > 0,
  });

  // Car search — mirrors CarListPage's debounced plate search.
  const [carPlateInput, setCarPlateInput] = useState('');
  const [carPlateQuery, setCarPlateQuery] = useState('');
  const [carId, setCarId] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setCarPlateQuery(carPlateInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [carPlateInput]);

  const { data: cars } = useQuery({
    queryKey: ['admin-cars-search', carPlateQuery],
    queryFn: () =>
      api.get<CarsResponse>(`/cars?limit=10&plate_number=${encodeURIComponent(carPlateQuery)}`),
    enabled: canManage && carPlateQuery.length > 0,
  });

  const { data: locations } = useQuery({
    queryKey: RESOURCE_KEYS.location.list,
    queryFn: () => api.get<LocationsResponse>('/locations'),
    enabled: canManage,
  });
  const [locationId, setLocationId] = useState('');

  // Deliberately no `min` on these — unlike the customer app's picker, a past pickup/return is
  // the entire point of this page (spec FR-003).
  const [pickupAt, setPickupAt] = useState('');
  const [returnAt, setReturnAt] = useState('');

  const [error, setError] = useState('');
  // 361 (FR-005): the trip answers are optional for staff. Sent only when all are answered; refused when half-done.
  const [tripStudents, setTripStudents] = useState('');
  const [tripPurpose, setTripPurpose] = useState('');
  const [tripDelivery, setTripDelivery] = useState('');
  const [tripNonHalal, setTripNonHalal] = useState('');

  const { data: preview } = useQuery<QuotePreview>({
    queryKey: ['admin-booking-create-preview', carId, pickupAt, returnAt],
    queryFn: () =>
      api.post<QuotePreview>('/pricing/quote', {
        car_id: carId,
        pickup_at: new Date(pickupAt).toISOString(),
        return_at: new Date(returnAt).toISOString(),
      }),
    enabled: canManage && Boolean(carId) && Boolean(pickupAt) && Boolean(returnAt),
  });

  const create = useSaveMutation<Record<string, unknown>, CreatedBooking>({
    resource: 'booking',
    mutationFn: (body) => api.post<CreatedBooking>('/bookings/admin-create', body),
    onSaved: (res) => {
      setError('');
      navigate(`/bookings/${res.id}`);
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to create booking',
      );
    },
  });

  if (!canManage) return null;

  function onSubmit() {
    if (!customerId) {
      setError('Search for and select the customer this booking belongs to.');
      return;
    }
    if (!carId) {
      setError('Search for and select a car.');
      return;
    }
    if (!locationId) {
      setError('Select a pickup location.');
      return;
    }
    if (!pickupAt || !returnAt) {
      setError('Pickup and return date/time are both required.');
      return;
    }
    if (!preview?.pricing_template_id || preview.outcome !== 'priced') {
      setError('No price could be resolved for this car and window.');
      return;
    }
    const anyTrip = Boolean(tripStudents.trim() || tripPurpose || tripDelivery || tripNonHalal);
    const students = /^\s*([1-5])\s*$/.exec(tripStudents)?.[1];
    const tripDone =
      students &&
      tripPurpose &&
      (tripDelivery === 'no' || (tripDelivery === 'yes' && tripNonHalal));
    if (anyTrip && !tripDone) {
      setError('Answer all the trip questions (students 1–5), or leave them all empty.');
      return;
    }
    const trip = tripDone
      ? {
          students: Number(students),
          purpose: tripPurpose,
          food_delivery: tripDelivery === 'yes',
          ...(tripDelivery === 'yes' ? { non_halal: tripNonHalal === 'yes' } : {}),
        }
      : undefined;
    void create.save({
      ...(trip ? { trip } : {}),
      user_id: customerId,
      car_id: carId,
      location_id: locationId,
      pickup_at: new Date(pickupAt).toISOString(),
      return_at: new Date(returnAt).toISOString(),
      pricing_template_id: preview.pricing_template_id,
    });
  }

  const customerOptions = customers?.items ?? [];
  const carOptions = cars?.items ?? [];
  const selectedCustomer = customerOptions.find((c) => c._id === customerId);

  return (
    <div className="max-w-2xl">
      <PageHeader title="Create booking" fallback="/bookings" />

      <Card className="grid gap-4">
        <FormField label="Customer" name="customer_email" required hint="Search by email">
          <Input
            id="customer_email"
            name="customer_email"
            type="email"
            placeholder="customer@example.com"
            value={customerEmailInput}
            onChange={(e) => {
              setCustomerEmailInput(e.currentTarget.value);
              setCustomerId('');
            }}
          />
        </FormField>
        {customerOptions.length > 0 && !customerId && (
          <div className="grid gap-1">
            {customerOptions.map((c) => (
              <button
                key={c._id}
                type="button"
                className="text-left text-sm px-3 py-2 rounded border border-hairline hover:bg-surface-alt"
                onClick={() => {
                  setCustomerId(c._id);
                  setCustomerEmailInput(c.email);
                }}
              >
                {c.first_name} {c.last_name} — {c.email}
                {c.identity_verification?.status !== 'approved' && (
                  <span className="ml-2 text-amber-600">
                    (identity: {c.identity_verification?.status ?? 'pending'})
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        {selectedCustomer && (
          <p className="text-sm text-steel">
            Selected: {selectedCustomer.first_name} {selectedCustomer.last_name} (
            {selectedCustomer.email})
          </p>
        )}
        {/* 393 (#1313, FR-008): a warning only; staff may still create the booking. Penalties are read only by those
            who may read them platform-wide (412; the list refuses anyone else). */}
        {selectedCustomer && holdsAnywhere(ability as never, 'read', 'Penalty') && (
          <UnpaidPenaltyWarning
            userId={selectedCustomer._id}
            name={`${selectedCustomer.first_name ?? ''} ${selectedCustomer.last_name ?? ''}`.trim()}
          />
        )}

        <FormField label="Car" name="car_plate" required hint="Search by plate number">
          <Input
            id="car_plate"
            name="car_plate"
            type="text"
            placeholder="Plate number"
            value={carPlateInput}
            onChange={(e) => {
              setCarPlateInput(e.currentTarget.value);
              setCarId('');
            }}
          />
        </FormField>
        {carOptions.length > 0 && !carId && (
          <div className="grid gap-1">
            {carOptions.map((c) => (
              <button
                key={c._id}
                type="button"
                className="text-left text-sm px-3 py-2 rounded border border-hairline hover:bg-surface-alt"
                onClick={() => {
                  setCarId(c._id);
                  setCarPlateInput(c.plate_number);
                }}
              >
                {c.name} — {c.plate_number}
              </button>
            ))}
          </div>
        )}

        <FormField label="Pickup location" name="location_id" required>
          <Select
            id="location_id"
            name="location_id"
            value={locationId}
            onChange={(e) => setLocationId(e.currentTarget.value)}
          >
            <option value="">Select a location</option>
            {(locations?.items ?? []).map((l) => (
              <option key={l._id} value={l._id}>
                {l.name}
              </option>
            ))}
          </Select>
        </FormField>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Pickup date/time" name="pickup_at" required>
            <Input
              id="pickup_at"
              name="pickup_at"
              type="datetime-local"
              value={pickupAt}
              onChange={(e) => setPickupAt(e.currentTarget.value)}
            />
          </FormField>
          <FormField label="Return date/time" name="return_at" required>
            <Input
              id="return_at"
              name="return_at"
              type="datetime-local"
              value={returnAt}
              onChange={(e) => setReturnAt(e.currentTarget.value)}
            />
          </FormField>
        </div>

        {/* 361 (FR-005): the customer's trip answers, optional for staff (a phone booking is never blocked). */}
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Students in the car (optional)" name="trip_students">
            <Input
              id="trip_students"
              name="trip_students"
              inputMode="numeric"
              placeholder="1–5"
              value={tripStudents}
              onChange={(e) => setTripStudents(e.currentTarget.value)}
            />
          </FormField>
          <FormField label="Trip purpose (optional)" name="trip_purpose">
            <Select
              id="trip_purpose"
              name="trip_purpose"
              value={tripPurpose}
              onChange={(e) => setTripPurpose(e.currentTarget.value)}
            >
              <option value="">Not answered</option>
              {TRIP_PURPOSES.map((p) => (
                <option key={p} value={p}>
                  {TRIP_PURPOSE_LABELS[p]}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="OK to do food delivery (optional)" name="trip_delivery">
            <Select
              id="trip_delivery"
              name="trip_delivery"
              value={tripDelivery}
              onChange={(e) => {
                setTripDelivery(e.currentTarget.value);
                if (e.currentTarget.value !== 'yes') setTripNonHalal('');
              }}
            >
              <option value="">Not answered</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
          </FormField>
          {tripDelivery === 'yes' && (
            <FormField label="OK with non-halal food" name="trip_non_halal">
              <Select
                id="trip_non_halal"
                name="trip_non_halal"
                value={tripNonHalal}
                onChange={(e) => setTripNonHalal(e.currentTarget.value)}
              >
                <option value="">Not answered</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Select>
            </FormField>
          )}
        </div>

        {carId && pickupAt && returnAt && (
          <p className="text-sm text-steel" data-testid="price-preview">
            {preview?.outcome === 'priced'
              ? `Price: ${preview.currency_symbol ?? ''} ${((preview.grand_total_minor ?? preview.total_minor ?? 0) / 100).toFixed(2)}`
              : 'Resolving price…'}
          </p>
        )}

        {/* 386 (#1278, FR-011): a warning only — staff may still create the booking. */}
        <GapWarning
          carId={carId || null}
          pickupAt={pickupAt ? new Date(pickupAt).toISOString() : null}
          returnAt={returnAt ? new Date(returnAt).toISOString() : null}
        />

        <div>
          <Button loading={create.isPending} onClick={onSubmit}>
            Create booking
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </Card>
    </div>
  );
}
